import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Checkbox, Tooltip } from 'antd';
import { BookOutlined, TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RESPONSE_SOURCES } from './responseHelpers.js';

/**
 * Prefill of poll responses from predefined sources
 * (teaching staff, study subjects).
 */
const ResponsesImport = ({
  activeSource,
  isTeachingStaffLoading,
  isSubjectsLoading,
  onImportTeachingStaff,
  onImportSubjects,
}) => {
  const { t } = useTranslation();
  const [includeExternal, setIncludeExternal] = useState(false);
  const [includePhd, setIncludePhd] = useState(false);

  const isLocked = (source) => activeSource !== null && activeSource !== source;

  /**
   * Disabled antd buttons do not fire mouse events, so the tooltip
   * is attached to a wrapping span.
   */
  const withLockTooltip = (source, button) =>
    isLocked(source) ? (
      <Tooltip
        title={t(
          'Odpovede už boli načítané z iného zdroja. Pre zmenu zdroja najprv vymažte všetky odpovede.',
        )}
      >
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );

  return (
    <Alert
      className="responses-import"
      type="info"
      showIcon
      title={t('Predvyplnenie odpovedí')}
      description={
        <>
          <p>
            {t(
              'Odpovede kolekcie môžete namiesto ručného zadávania načítať z jedného z nasledujúcich zdrojov. Kolekcia má obsahovať odpovede rovnakého typu, preto je možné vybrať len jednu možnosť: po načítaní jedného zdroja sa druhý zablokuje. Ak chcete zdroj zmeniť, vymažte v tabuľke všetky odpovede.',
            )}
          </p>
          <p>
            {t(
              'Načítané položky sa pridajú na koniec zoznamu, položky, ktoré už v zozname sú, sa nepridajú znova (vybraný zdroj je teda možné načítať opakovane, napr. po zmene voľby). Pred uložením ich môžete v tabuľke upraviť alebo vymazať.',
            )}
          </p>
          <div className="responses-import-source">
            <span>
              <b>1. {t('Pedagogickí zamestnanci')}</b>
              {' – '}
              {t(
                'Mená všetkých pedagogických zamestnancov (učitelia a doktorandi) s titulmi.',
              )}
            </span>
            <div className="responses-import-controls">
              <Checkbox
                checked={includeExternal}
                disabled={isLocked(RESPONSE_SOURCES.TEACHING_STAFF)}
                onChange={(e) => setIncludeExternal(e.target.checked)}
              >
                {t('Vrátane externých doktorandov')}
              </Checkbox>
              {withLockTooltip(
                RESPONSE_SOURCES.TEACHING_STAFF,
                <Button
                  icon={<TeamOutlined />}
                  loading={isTeachingStaffLoading}
                  disabled={isLocked(RESPONSE_SOURCES.TEACHING_STAFF)}
                  onClick={() => onImportTeachingStaff(includeExternal)}
                >
                  {t('Načítať pedagogických zamestnancov')}
                </Button>,
              )}
            </div>
          </div>
          <div className="responses-import-source">
            <span>
              <b>2. {t('Predmety')}</b>
              {' – '}
              {t('Názvy predmetov všetkých študijných programov fakulty.')}
            </span>
            <div className="responses-import-controls">
              <Checkbox
                checked={includePhd}
                disabled={isLocked(RESPONSE_SOURCES.SUBJECTS)}
                onChange={(e) => setIncludePhd(e.target.checked)}
              >
                {t('Vrátane doktorandského štúdia')}
              </Checkbox>
              {withLockTooltip(
                RESPONSE_SOURCES.SUBJECTS,
                <Button
                  icon={<BookOutlined />}
                  loading={isSubjectsLoading}
                  disabled={isLocked(RESPONSE_SOURCES.SUBJECTS)}
                  onClick={() => onImportSubjects(includePhd)}
                >
                  {t('Načítať predmety')}
                </Button>,
              )}
            </div>
          </div>
        </>
      }
    />
  );
};

ResponsesImport.propTypes = {
  activeSource: PropTypes.oneOf([...Object.values(RESPONSE_SOURCES), null]),
  isTeachingStaffLoading: PropTypes.bool,
  isSubjectsLoading: PropTypes.bool,
  onImportTeachingStaff: PropTypes.func.isRequired,
  onImportSubjects: PropTypes.func.isRequired,
};

export default ResponsesImport;
